import sseClientActivityMonitorStyles from './sse-client-activity-monitor.styles.css?inline';
import { customElement } from 'lit/decorators.js';
import { html, LitElement, unsafeCSS } from 'lit';
import { NetworkStateNotifier } from '../../utility/notifications/network-state-notifier.js';
import { TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import { RECONNECTING_LABEL } from '../../utility/notifications/connection-health-words';

/**
 * Compact network / SSE status indicator.
 * Visual design goals:
 *  - Minimal footprint (single 10-12px dot) so it does not visually fight the gradient header.
 *  - High-contrast ring for visibility on both light & dark gradient stops.
 *  - Distinct motion only while "updating" to reduce distraction.
 *  - Accessible text announced on state change.
 */
@customElement('sse-client-activity-monitor')
export class SSEClientActivityMonitor extends LitElement {
  static styles = [unsafeCSS(sseClientActivityMonitorStyles)];

  /** Debug helper: when present forces component into offline visual state. */
  @property({ type: Boolean, attribute: 'force-offline' }) forceOffline = false;
  /** Debug helper: when present forces component into updating visual state (overrides online). */
  @property({ type: Boolean, attribute: 'force-updating' })
  forceUpdating = false;
  /** Overrides prefers-reduced-motion for this instance when set. */
  @property({ type: Boolean, attribute: 'force-motion' }) forceMotion = false;
  /** Unified override for state: 'online' | 'updating' | 'offline'. Takes precedence over other force flags if set. */
  @property({ attribute: 'force-state' }) forceState?:
    'online' | 'updating' | 'offline';

  protected get updating(): boolean {
    return NetworkStateNotifier.getInstance().updating;
  }
  protected get online(): boolean {
    return NetworkStateNotifier.getInstance().online;
  }
  /**
   * Live updates down while the server answers and the sign-in holds (D-137, rule 2): shown here,
   * quietly, and nowhere that blocks. Includes the minute-plus case, where the page's own notice
   * says more; the dot stays the same, because the condition has not changed, only lasted.
   */
  protected get reconnecting(): boolean {
    const health = NetworkStateNotifier.getInstance().health;
    return health === 'reconnecting' || health === 'live-updates-failing';
  }

  private _lastAnnounced: string | undefined;

  connectedCallback(): void {
    super.connectedCallback();
    NetworkStateNotifier.getInstance().register(this);
  }
  disconnectedCallback(): void {
    NetworkStateNotifier.getInstance().unRegister(this);
    super.disconnectedCallback();
  }

  /** Derive current state keyword. */
  private get state(): 'offline' | 'reconnecting' | 'updating' | 'online' {
    if (this.forceState === 'offline') return 'offline';
    if (this.forceState === 'updating') return 'updating';
    if (this.forceState === 'online') return 'online';
    if (this.forceOffline) return 'offline';
    if (this.forceUpdating) return 'updating';
    if (!this.online) return 'offline';
    if (this.reconnecting) return 'reconnecting';
    if (this.updating) return 'updating';
    return 'online';
  }

  /** Friendly phrase for screen readers / tooltip legend reuse. */
  private get stateLabel(): string {
    switch (this.state) {
      case 'reconnecting':
        return RECONNECTING_LABEL;
      case 'offline':
        return 'Offline – attempting to reconnect';
      case 'updating':
        return 'Receiving updates – changes will appear shortly';
      default:
        return 'Online';
    }
  }

  /** Creates the tooltip legend content. */
  private legend(): TemplateResult {
    return html`
      <div class="legend" role="presentation">
        <div class="legend-row">
          <span class="swatch online" aria-hidden="true"></span>
          <span class="legend-label">Online</span>
        </div>
        <div class="legend-row">
          <span class="swatch updating" aria-hidden="true"></span>
          <span class="legend-label">
            Receiving updates – changes will appear shortly
          </span>
        </div>
        <div class="legend-row">
          <span class="swatch reconnecting" aria-hidden="true"></span>
          <span class="legend-label">${RECONNECTING_LABEL}</span>
        </div>
        <div class="legend-row">
          <span class="swatch offline" aria-hidden="true"></span>
          <span class="legend-label">Offline – attempting to reconnect</span>
        </div>
      </div>
    `;
  }

  /** Announce state changes politely. */
  protected updated(): void {
    const phrase = this.stateLabel;
    if (phrase !== this._lastAnnounced) {
      const live = this.renderRoot?.querySelector<HTMLSpanElement>('.sr-live');
      if (live) live.textContent = phrase;
      this._lastAnnounced = phrase;
    }
  }

  render(): TemplateResult {
    // Dot element; animation handled via attribute selectors in CSS.
    const hostClasses = [
      this.forceMotion ? 'force-motion' : '',
      this.forceOffline ? 'force-offline' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return html`
      <sl-tooltip placement="left" hoist class="${hostClasses}">
        <div slot="content">${this.legend()}</div>
        <span
          class="status-dot ${this.state}"
          role="img"
          aria-label="${this.stateLabel}"
        ></span>
      </sl-tooltip>
      <span class="sr-live" aria-live="polite" aria-atomic="true"></span>
    `;
  }
}

// (Kept exported helper for any existing imports; now returns a generic updating glyph.)
export function dataPendingUpdateIcon(active: boolean = true): TemplateResult {
  return html`<span
    class="status-dot updating ${active ? 'active' : ''}"
    aria-label="Receiving updates"
  ></span>`;
}
